import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import Box from '@ff/ui/Box';
import FadeSlide, { Props as SlideProps } from '@ff/ui/SlideShow/presets/FadeSlideShow/FadeSlide';
import SimpleSlide from '@ff/ui/SlideShow/Slide';
import Specs, { Props as SpecsProps } from './Specs';
import type { Props as DetailSectionProps } from './DetailSection';
import type { Props as HighlightsDetailProps } from './HighlightsDetail/highlightsDetailProps';
import Heading from './Heading';
import type { ColorTheme } from '@ff/ui/types';
import ButtonLink from './ButtonLink';
import Animate from './Animate';
import type { Props as PropsSchema, Position } from './props-schema';
import Text from '@ff/ui/Text';
import Link from '@ff/ui/Link';
import useIsBreakpoint from '@ff/ui/utils/useIsBreakpoint';
import { SLIDE_MIN_HEIGHT } from '@ff/ui/SlideShow/constants';
import withSafeAreaInsetBottom from 'shared/lib/withSafeAreaInsetBottom';

export interface Props {
  id?: SlideProps['id'];
  index: number;
  editor?: boolean;
  navigatorLabel?: string;
  navigatorTheme?: ColorTheme;
  imageUrl?: PropsSchema['slides'][0]['imageUrl'];
  topSubtitle?: string;
  title?: string;
  subtitle?: string;
  subtitleCarousel?: PropsSchema['slides'][0]['subtitleCarousel'];
  infoBlock?: PropsSchema['slides'][0]['infoBlock'];
  specs?: SpecsProps['specs'];
  cta?: PropsSchema['slides'][0]['cta']
  detail?: DetailSectionProps;
  highlights?: HighlightsDetailProps;
  alwaysShowDownArrow?: boolean;
  enabledDownArrow?: boolean;
  slideScrollSnapAnimation?: boolean;
}

// TODO: refactor SlideContent and SlideContentDetail
export default function SlideContent(props: Props) {
  const {
    id,
    index: slideIndex,
    editor,
    topSubtitle,
    title,
    subtitle,
    subtitleCarousel,
    infoBlock,
    specs,
    cta,
    imageUrl,
    alwaysShowDownArrow,
    enabledDownArrow,
    slideScrollSnapAnimation
  } = props;

  const isMd = useIsBreakpoint('md');

  const animate = useShouldAnimate(slideIndex);

  let Slide = FadeSlide;

  if (editor || !slideScrollSnapAnimation) {
    Slide = SimpleSlide;
  }

  const location = isMd ? infoBlock?.position?.md : infoBlock?.position?._;

  const slideProps = {
    id,
    index: slideIndex,
    // Only send the image sizes to the Slide
    imageUrl: {
      _: imageUrl._,
      md: imageUrl.md
    },
    imageAlt: imageUrl.imageAlt,
    alwaysShowDownArrow,
    enabledDownArrow
  }

  // --------- FOR MEMBERSHIP PAGE --------------------------------
  if (infoBlock && (infoBlock.title || infoBlock.topSubtitle || infoBlock.bottomSubtitle || infoBlock.cta?.label)) {
    return (
      <Slide {...slideProps} scrollSnapAnimation={slideScrollSnapAnimation}>
        <PositionContent location={location}>
          <InnerContent location={location} slideId={id} >
            <StyledText
              fontSize='16px' mb='6px'
              colorTheme={infoBlock.colorTheme}
              location={location}
              fontWeight='300'
            >
              {infoBlock.topSubtitle}
            </StyledText>
            <StyledText
              fontSize={{ _: 30, md: 40 }}
              mb='6px'
              fontWeight='300'
              colorTheme={infoBlock.colorTheme}
              location={location}
            >
              {infoBlock.title}
            </StyledText>
            <StyledText
              fontWeight='300'
              fontSize={{ _: 14, md: 16 }}
              mb='32px'
              colorTheme={infoBlock.colorTheme}
              location={location}
            >
              {infoBlock.bottomSubtitle}
            </StyledText>
            <InfoListContainer mb='32px'>
              {
                infoBlock.infoList?.map((item) => (
                  <Box key={item.title} display='flex' flexDirection='column' alignItems='center'>
                    <StyledText fontSize='28px' lineHeight='36px' colorTheme={infoBlock.colorTheme}>{item.title}</StyledText>
                    <StyledText fontSize='12px' lineHeight='20px' colorTheme={infoBlock.colorTheme}>{item.subtitle}</StyledText>
                  </Box>
                ))
              }
            </InfoListContainer>
            {infoBlock.cta?.label &&
              <StyledLink
                id={`infoBlock-cta-${id}`}
                variant='outlineClearButton'
                href={infoBlock.cta?.url}
                appHandlerKey={infoBlock.cta?.appHandlerKey}
                colorTheme={infoBlock.colorTheme}
                hideOnMobile={infoBlock.cta?.hideOnMobile}
              >
                {infoBlock.cta.label}
              </StyledLink>
            }
          </InnerContent>
        </PositionContent>
      </Slide>
    )
  }
  // --------- FOR MEMBERSHIP PAGE --------------------------------

  return (
    specs?.length > 0
      ?
      // ------------ product page, first slide -----------------------
      <Slide {...slideProps} scrollSnapAnimation={slideScrollSnapAnimation} gradientPercent={20}>
        <Content slideScrollSnapAnimation={slideScrollSnapAnimation}>
          <TopContent>
            <Box marginTop='15vh'>
              <Heading topSubtitle={topSubtitle} title={title} subtitle={subtitle} subtitleCarousel={subtitleCarousel} />
            </Box>
            {/* filler Box to expand top and bottom contents */}
            <Box flex='1'></Box>
            <SpecsAndButtonContainer>
              <Specs specs={specs} />
            </SpecsAndButtonContainer>
          </TopContent>
          {cta?.length > 0 &&
            <ButtonContainer display={{ _: 'none', md: 'flex' }}>
              {cta?.map((item, buttonIndex) => (
                <AnimatedButton
                  key={item.text}
                  url={item.url}
                  disabled={item.disabled}
                  appHandlerKey={item.appHandlerKey}
                  index={buttonIndex}
                  animate={false}
                >
                  {item.text}
                </AnimatedButton>
              ))}
            </ButtonContainer>
          }
        </Content>
      </Slide>
      // ------------ home page -----------------------
      :
      <Slide {...slideProps} scrollSnapAnimation={slideScrollSnapAnimation}>
        <Content slideScrollSnapAnimation={slideScrollSnapAnimation}>
          <Box marginTop='15vh'>
            <Heading topSubtitle={topSubtitle} title={title} subtitle={subtitle} subtitleCarousel={subtitleCarousel} animate={animate} />
          </Box>
          {/* filler Box to expand top and bottom contents */}
          <Box flex={1}></Box>
          {cta?.length > 0 &&
            <ButtonContainer display='flex'>
              {cta?.map((item, buttonIndex) => (
                <AnimatedButton
                  key={item.text}
                  url={item.url}
                  disabled={item.disabled}
                  appHandlerKey={item.appHandlerKey}
                  index={buttonIndex}
                  animate={animate}
                >
                  {item.text}
                </AnimatedButton>
              ))}
            </ButtonContainer>
          }
        </Content>
      </Slide>
  );
}

function AnimatedButton(props) {
  const { url, children, index, animate, appHandlerKey, disabled } = props;

  const keyframe = useMemo(() => {
    return {
      from: {
        opacity: 0,
        /* ---------------------- WARNING ------------------------- */
        /* buttons going outside of the width would cause flicking during scroll on android device */
        transform: `translateX(${index === 0 ? '-' : '+'}30px)`
      },
      to: {
        opacity: 1,
        transform: 'translateX(0px)'
      },
    }

  }, [index]);

  return (
    <Animate
      animate={animate}
      duration={1000}
      keyframes={keyframe}
    >
      {/* TODO: Keyframes ref error, Keyframes is looking for ref of children(still work without ref) */}
      <ButtonLink
        href={url}
        disabled={disabled}
        appHandlerKey={appHandlerKey}
        width={{ _: '163px', sm: '264px' }}
      >
        {children}
      </ButtonLink>
    </Animate>
  )
}

function useShouldAnimate(index: number) {
  // only animate on load
  const [hasAnimate, setHasAnimate] = useState(false);

  useEffect(() => {
    if (!hasAnimate) {
      setTimeout(() => {
        setHasAnimate(true);
      }, 2000); // let animation finish
    }
  }, [hasAnimate]);

  // only animate first slide
  const animate = !hasAnimate && index === 0;
  return animate;
}

const Content = styled(Box)<{ slideScrollSnapAnimation?: boolean }>(({ theme, slideScrollSnapAnimation }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: slideScrollSnapAnimation ? 'var(--doc-height)' : 'auto',
  minHeight: slideScrollSnapAnimation ? SLIDE_MIN_HEIGHT : `max(${SLIDE_MIN_HEIGHT}px, 100vh)`,
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  // items going over the width would cause flicking in android
  maxWidth: '100vw',
  overflow: 'hidden',
  /* items going over the width would cause flicking in android */
  paddingBottom: withSafeAreaInsetBottom('60px'),

  [theme.mediaQueries.md]: {
    paddingBottom: '15vh',
  }
}));

const PositionContent = styled(Box)<{ location?: Position }>(({ theme, location }) => {
  let justifyContent = 'center';
  let alignItems = 'center';

  if (location === 'TOP') {
    justifyContent = 'flex-start';
    alignItems = 'center';
  } else if (location === 'RIGHT') {
    justifyContent = 'center';
    alignItems = 'flex-end';
  } else if (location === 'BOTTOM') {
    justifyContent = 'flex-end';
    alignItems = 'center';
  } else if (location === 'LEFT') {
    justifyContent = 'center';
    alignItems = 'flex-start';
  }

  return {
    display: 'flex',
    flexDirection: 'column',
    height: 'var(--doc-height)',
    minHeight: SLIDE_MIN_HEIGHT,
    justifyContent,
    alignItems,
    position: 'relative',
    padding: '75px 16px 45px 16px',

    [theme.mediaQueries.md]: {
      padding: 50
    },

    [theme.mediaQueries.lg]: {
      padding: 136
    }
  }
});

const InnerContent = styled(Box)<{ location?: Position, slideId: string }>(({ theme, location, slideId }) => {
  let alignItems = 'center';

  if (location === 'TOP') {
    alignItems = 'center';
  } else if (location === 'RIGHT') {
    alignItems = 'flex-start';
  } else if (location === 'BOTTOM') {
    alignItems = 'center';
  } else if (location === 'LEFT') {
    alignItems = 'flex-start';
  }

  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems,
    maxWidth: (location === 'LEFT' || location === 'RIGHT') ? 'max(221px, 50vw)' : undefined,

    paddingTop: location === 'TOP' ? '70px' : undefined,
    flex: location === 'TOP' ? 1 : 'none',
    [`& > #infoBlock-cta-${slideId}`]: location === 'TOP' ? { position: 'absolute', bottom: withSafeAreaInsetBottom('15px') } : { position: undefined, bottom: undefined },

    [theme.mediaQueries.md]: {
      maxWidth: (location === 'LEFT' || location === 'RIGHT') ? '40vw' : undefined,

      paddingTop: undefined,
      flex: 'none',
      '& > #infoBlock-cta': { position: undefined, bottom: undefined },
    },
  }
});

const InfoListContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',

  [theme.mediaQueries.md]: {
    flexDirection: 'row',
    gap: '64px'
  }
}));

const StyledText = styled(Text)<{
  colorTheme?: ColorTheme,
  location?: Position
}>(({ colorTheme, location }) => ({
  color: colorTheme === 'DARK' ? 'white' : 'rgba(23, 26, 32, 1)',
  textAlign: (location === 'TOP' || location === 'BOTTOM' || location === 'CENTER') ? 'center' : undefined
}));

const StyledLink = styled(ButtonLink)<{ colorTheme?: ColorTheme, hideOnMobile?: boolean }>(({ theme, colorTheme, hideOnMobile }) => ({
  minWidth: 'min(163px, 80vw)',
  color: colorTheme === 'DARK' ? 'white' : 'rgba(23, 26, 32, 1)',
  borderColor: colorTheme === 'DARK' ? 'white' : 'rgba(23, 26, 32, 1)',
  display: hideOnMobile ? 'none' : 'flex',

  [theme.mediaQueries.md]: {
    minWidth: 'min(264px, 80vw)',
    display: 'flex'
  }
}));

const ButtonContainer = styled(Box)({
  gap: '24px',
  justifyContent: 'center',
  paddingLeft: '9px',
  paddingRight: '9px',
  /* ---------------------- WARNING ------------------------- */
  /* buttons going outside of the width would cause flicking during scroll on android device */
  width: '99vw'
});

const TopContent = styled(Box)({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  justifyContent: 'center',
  flex: 1,
});

const SpecsAndButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});
